<template>
	<LayoutScreen background="--background-base" max-width="500px">
		<!-- Figma 7415:16754: the close button 8 from the top, 20 from the right; the tile 104 from the top
		     (48 under this 56px row), title and field 24 apart. -->
		<template #header>
			<LayoutFlex padding="var(--size-8) var(--size-20)" gap="0" align-items="flex-end">
				<button type="button" class="feedback__close" aria-label="Close" @click="onSkip">
					<span class="feedback__close-icon"><img :src="closeGrey" alt="" width="13.6" height="13.6" /></span>
				</button>
			</LayoutFlex>
		</template>

		<template #content>
			<LayoutFlex padding="var(--size-48) var(--size-20) var(--size-24)" gap="var(--size-24)" align-items="center">
				<div class="feedback__icon" aria-hidden="true">
					<span class="feedback__pencil"><img :src="pencil" alt="" width="22.5654" height="23.0869" /></span>
				</div>

				<LayoutFlex padding="0" gap="0" align-items="center">
					<Text font="--heading-h3" color="--text-dark-primary" text-align="center">We welcome your feedback!</Text>
					<Text font="--heading-h3" color="--text-dark-primary" text-align="center">
						How could we improve Kova
						<br />
						for others?
					</Text>
				</LayoutFlex>

				<textarea
					v-model="feedback"
					class="feedback__textarea"
					placeholder="e.g. propose a feature you definitely missed"
				/>
			</LayoutFlex>
		</template>

		<template #footer>
			<LayoutFlex padding="0 var(--size-20) var(--size-24)" gap="0">
				<Button
					full-width
					:disabled="(!geo.isOneClickCancelRequired && !feedback.trim()) || submitting"
					@ds:click="onSubmit"
				>
					<Text font="--text-16-18-semi">{{ submitting ? 'Submitting…' : 'Submit' }}</Text>
				</Button>
			</LayoutFlex>
		</template>
	</LayoutScreen>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { Button, LayoutFlex, LayoutScreen, Text } from '@simple-life-app/growth-ui-kova'
import closeGrey from '@/assets/icons/close-grey.svg'
import pencil from '@/assets/icons/pencil.svg'
import { useGeoStore } from '@/store/geo'
import { STEP_TITLE, useCancelStepLogging } from '@/analytics'

const router = useRouter()
const geo = useGeoStore()

const feedback = ref('')
const submitting = ref(false)

// FEEDBACK step: responses are always [], the free text rides in data.text. Snapshot the
// text on every change so CancellationFlowStepDone (fired on leave) reports the latest value.
const step = useCancelStepLogging(STEP_TITLE.FEEDBACK)
watch(feedback, (value) => step.record([], value))

// TODO: when the feedback endpoint is wired up, replace the local fake-submit
// with a real call (likely POST /feedback or similar). For now we just log
// and route the user back to /status — the cancellation itself has already
// landed by the time this screen renders.
async function onSubmit() {
	if (!feedback.value.trim() && !geo.isOneClickCancelRequired) return
	submitting.value = true
	try {
		// eslint-disable-next-line no-console
		console.info('[feedback] submitted', { text: feedback.value })
		await new Promise((resolve) => setTimeout(resolve, 150))
		router.push('/status')
	} finally {
		submitting.value = false
	}
}

function onSkip() {
	router.push('/status')
}
</script>

<style scoped>
/* Figma "Icon": 20 around a 24px icon-edit, radius 24, `status/purple/bg` rgba(162, 38, 166, 0.1). */
.feedback__icon {
	display: flex;
	padding: var(--size-20);
	background: rgba(162, 38, 166, 0.1);
	border-radius: var(--size-24);
}

/* icon-edit: the pencil sits 0.44px in from the left and 0.21px from the top of its 24px box. */
.feedback__pencil {
	position: relative;
	width: var(--size-24);
	height: var(--size-24);
}

.feedback__pencil img {
	position: absolute;
	top: 0.21px;
	left: 0.44px;
}

.feedback__close {
	appearance: none;
	width: 40px;
	height: 40px;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	padding: 0;
	background: var(--background-white);
	color: var(--text-dark-primary);
	border: none;
	border-radius: var(--size-16);
	cursor: pointer;
	transition: filter 0.15s ease;
}

.feedback__close:hover {
	filter: brightness(0.96);
}

/* The 16px Kova icon-close in navy 55% (baked into the asset): a plus with a gap, turned 45°. */
.feedback__close-icon {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: var(--size-16);
	height: var(--size-16);
}

.feedback__close-icon img {
	transform: rotate(45deg);
}

/* Figma Input_Field 7415:16765: 112 tall. */
.feedback__textarea {
	width: 100%;
	height: 112px;
	padding: var(--size-12) var(--size-16);
	border: 2px solid rgba(50, 54, 83, 0.1);
	border-radius: var(--size-16);
	background: var(--background-white);
	font: var(--text-16-22-semi);
	color: var(--text-dark-primary);
	resize: none;
	box-sizing: border-box;
}

.feedback__textarea::placeholder {
	color: rgba(50, 54, 83, 0.4);
}

.feedback__textarea:focus {
	outline: none;
	border-color: var(--stroke-accent-brand);
}
</style>
